import { ALL_ITEMS_ID } from '@/lib/actionItems';
import { StorageEnum } from '@/lib/enum/StorageEnum';

export enum ScheduleGroupByEnum {
  None = 'companyId',
  Group = 'employeeGroupId',
  JobRole = 'defaultJobRoleId',
  Me = 'Me',
}

export enum ViewShiftTagModeEnum {
  None = 'none',
  All = 'all',
  Condensed = 'condensed',
}

export enum ViewShiftsByEnum {
  Employee = 'Employee',
  JobRole = 'JobRole',
  ShiftArea = 'ShiftArea',
}

/**
 * What the schedule insights count. The first five decide which shifts and time off
 * go into the hours and cost totals; the last three show or hide the integration
 * footer bars.
 */
export type ScheduleInsightsSettingsType = {
  unpublishedShifts: boolean;
  openShifts: boolean;
  unassignedShifts: boolean;
  filteredShiftsOnly: boolean;
  timeOff: boolean;
  sales: boolean;
  visitors: boolean;
  labour: boolean;
};

export type ScheduleViewOptionsType = {
  viewShiftsBy: ViewShiftsByEnum;
  groupEmployeesBy: ScheduleGroupByEnum;
  showUnscheduledEmployees: boolean;
  showUnpublishedShifts: boolean;
  pinMySchedule: boolean;
  viewShiftTagMode: ViewShiftTagModeEnum;
  showShiftJobRole: boolean;
  showShiftLocation: boolean;
  showBreaksAndAreas: boolean;
  insights: boolean;
  insightsSettings: ScheduleInsightsSettingsType;
  timeOffCalendar: boolean;
  showScheduledEvents: boolean;
  showProfilePhotos: boolean;
  showShiftsFromOtherSchedules: boolean;
};

export type SchedulePrintOptionsType = {
  selectedGroups: number[];
  allShiftEndTimes: boolean;
  scheduleEvents: boolean;
  unpublishedShifts: boolean;
  allShiftJobRoles: boolean;
  allShiftTags: boolean;
  shiftNotes: boolean;
  timeOff: boolean;
  unavailability: boolean;
};

/** Counts everything, as the insights did before they could be customised. */
export const getDefaultInsightsSettings = (): ScheduleInsightsSettingsType => ({
  unpublishedShifts: true,
  openShifts: true,
  unassignedShifts: true,
  filteredShiftsOnly: false,
  timeOff: true,
  sales: true,
  visitors: true,
  labour: true,
});

/** Settings saved before a newer one existed take that one's default. */
export const restoreInsightsSettings = (
  saved?: Partial<ScheduleInsightsSettingsType> | null,
): ScheduleInsightsSettingsType => ({
  ...getDefaultInsightsSettings(),
  ...saved,
});

export const getDefaultViewOptions = (
  userCanManageShifts: boolean,
): ScheduleViewOptionsType => ({
  viewShiftsBy: ViewShiftsByEnum.Employee,
  groupEmployeesBy: userCanManageShifts
    ? ScheduleGroupByEnum.None
    : ScheduleGroupByEnum.JobRole,
  showUnscheduledEmployees: true,
  showUnpublishedShifts: true,
  pinMySchedule: true,
  viewShiftTagMode: ViewShiftTagModeEnum.Condensed,
  showShiftJobRole: true,
  showShiftLocation: true,
  showBreaksAndAreas: false,
  insights: true,
  insightsSettings: getDefaultInsightsSettings(),
  timeOffCalendar: false,
  showScheduledEvents: true,
  showProfilePhotos: true,
  showShiftsFromOtherSchedules: true,
});

export const getDefaultPrintOptions = (
  userCanManageShifts: boolean,
): SchedulePrintOptionsType => ({
  selectedGroups: [ALL_ITEMS_ID],
  allShiftEndTimes: false,
  scheduleEvents: true,
  unpublishedShifts: userCanManageShifts,
  allShiftJobRoles: false,
  allShiftTags: false,
  shiftNotes: false,
  timeOff: false,
  unavailability: false,
});

type OptionType =
  | boolean
  | ScheduleGroupByEnum
  | ViewShiftTagModeEnum
  | ScheduleInsightsSettingsType;
export type SetViewOption = (
  key: keyof ScheduleViewOptionsType,
  value: OptionType,
) => void;

export const storedViewOptionsId = (
  companyId: number,
  employeeId: number,
): string => `${StorageEnum.ScheduleViewOptions}C${companyId}E${employeeId}`;
